import PropTypes from 'prop-types';
import React, { useMemo, useState, useCallback, useRef } from 'react';
import loadable from '@loadable/component';
import { Check } from 'lucide-react';
import { fromSeoUrl } from 'helpers/HelperFunctions';
import { trackEvent } from 'helpers/Analytics';
import SingleLocationDropdown from 'components/SingleLocationDropdown/SingleLocationDropdown';
import Switch from 'components/Switch/Switch';
import AriAvatar from 'containers/Ari/AriAvatar';
import {
  getPopularJobCountriesShort,
  POPULAR_JOB_ROLES,
} from 'constants/popularJobLinks';
import {
  EXPERIENCE_LEVEL_OPTIONS,
  isExperienceGroupChecked,
} from './experienceLevelOptions';
import {
  isRoleSelected,
  normalizeRoleKey,
  resolveSelectedRole,
} from './roleFilterUtils';
import styles from './JobsFilterPanel.module.scss';

const ChooseRolesDialog = loadable(() => import('./ChooseRolesDialog'));

const INITIAL_VISIBLE_ROLES = 8;

/**
 * Filter and order panel for the jobs board. Rendered twice by layout
 * (desktop column + mobile sheet) with the same props — never different content.
 */
const JobsFilterPanel = ({
  filter,
  suggestedRoles,
  featuredRoles,
  pageType,
  auth,
  filterCount,
  idPrefix,
  showPanelHeader,
  onRoleToggle,
  onRolesApply,
  onExperienceGroupToggle,
  onHiringCapacityToggle,
  onRemoteFilterToggle,
  onInternalFilterToggle,
  onStatusFilterToggle,
  onLocationTextChange,
  onLocationChange,
  onFiltersReset,
  onBestMatchNavigate,
  onNewestFirstSelect,
  bestMatchActive,
}) => {
  const [rolesDialogOpen, setRolesDialogOpen] = useState(false);
  const [rolesDialogMounted, setRolesDialogMounted] = useState(false);
  const moreRolesButtonRef = useRef(null);
  const sectionId = (suffix) =>
    idPrefix ? `jobs-filter-${idPrefix}-${suffix}` : `jobs-filter-${suffix}`;

  const roleOptions = useMemo(() => {
    const seen = new Set();
    const options = [];

    const pushRole = (name, slug) => {
      if (!name) return;
      const key = normalizeRoleKey(name);
      const slugKey = normalizeRoleKey(slug);
      if (seen.has(key) || (slugKey && seen.has(slugKey))) return;
      seen.add(key);
      if (slugKey) seen.add(slugKey);
      options.push({ name, slug: slug || null });
    };

    (filter.selectedRoles || []).forEach((selected) => {
      const resolved = resolveSelectedRole(selected, suggestedRoles);
      pushRole(resolved.name, resolved.slug);
    });

    (featuredRoles || []).forEach((name) => pushRole(name));

    POPULAR_JOB_ROLES.forEach((role) => pushRole(role.name, role.slug));

    (suggestedRoles || []).forEach((role) => {
      const name = role.name || role.label || role.value;
      const slug = role.slug_plural || role.slug || null;
      pushRole(name, slug);
    });

    return options;
  }, [featuredRoles, suggestedRoles, filter.selectedRoles]);

  // Column shows eight roles by default, plus any extra hoisted/ticked roles.
  // Full taxonomy lives in the Choose roles dialog (lazy), not in this DOM.
  const visibleRoleOptions = useMemo(() => {
    const selectedCount = (filter.selectedRoles || []).length;
    const limit = Math.max(INITIAL_VISIBLE_ROLES, selectedCount);
    return roleOptions.slice(0, limit);
  }, [roleOptions, filter.selectedRoles]);

  const currentLocation =
    filter.locationText && fromSeoUrl(filter.locationText);

  const handleLocationReset = useCallback(() => {
    onLocationTextChange({ city: '', country: '', text: '' });
  }, [onLocationTextChange]);

  const handlePopularCountryClick = useCallback(
    (country) => {
      const isActive =
        filter.locationText === country.slug ||
        (filter.country === country.label && !filter.city);
      if (isActive) {
        onLocationChange({ city: '', country: '', text: '' });
        return;
      }
      onLocationChange({ city: '', country: country.label, text: '' });
    },
    [filter.city, filter.country, filter.locationText, onLocationChange]
  );

  const handleBestMatch = (event) => {
    event.preventDefault();
    trackEvent('jobs-best-match-clicked', {
      source: 'panel',
      pageType,
      auth,
      filterCount,
    });
    if (onBestMatchNavigate) {
      onBestMatchNavigate();
    }
  };

  const handleNewestFirst = () => {
    trackEvent('jobs-newest-first-clicked', {
      source: 'panel',
      pageType,
      auth,
    });
    if (onNewestFirstSelect) {
      onNewestFirstSelect();
    }
  };

  const openRolesDialog = () => {
    setRolesDialogMounted(true);
    setRolesDialogOpen(true);
  };

  const closeRolesDialog = () => {
    setRolesDialogOpen(false);
  };

  const handleRolesApply = (pendingRoles) => {
    if (onRolesApply) {
      onRolesApply(pendingRoles);
    }
  };

  const clearAllDisabled = filterCount === 0;
  const showMoreRoles = roleOptions.length > INITIAL_VISIBLE_ROLES;

  const clearAllButton = (
    <button
      type="button"
      className={styles.clearAll}
      onClick={onFiltersReset}
      disabled={clearAllDisabled}
      data-testid="jobs-filter-clear-all"
    >
      Clear all
    </button>
  );

  const newestFirstContent = (
    <span className={styles.orderContent}>
      <span className={styles.orderLabel}>Newest first</span>
      <span className={styles.orderHint}>
        Every job that fits, latest first.
      </span>
    </span>
  );

  const bestMatchContent = (
    <span className={styles.orderContent}>
      <span className={styles.bestMatchTitleRow}>
        <AriAvatar size={22} className={styles.ariAvatar} />
        <span className={styles.orderLabel}>Best match</span>
        {auth === 'guest' && (
          <span className={styles.freeAccountTag}>Free account</span>
        )}
      </span>
      <span className={styles.orderHint}>
        Ari ranks jobs against your profile and explains each one.
      </span>
    </span>
  );

  return (
    <div className={styles.panel} data-testid="jobs-filter-panel">
      {showPanelHeader && (
        <div className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>Filter and order</h2>
          {!bestMatchActive && clearAllButton}
        </div>
      )}

      <section className={styles.section} aria-labelledby={sectionId('order')}>
        <h3 id={sectionId('order')} className={styles.sectionTitle}>
          Order your results
        </h3>
        <div
          className={styles.orderList}
          role="radiogroup"
          aria-label="Order your results"
        >
          {bestMatchActive ? (
            <button
              type="button"
              className={`${styles.orderOption} ${styles.orderOptionButton}`}
              role="radio"
              aria-checked="false"
              onClick={handleNewestFirst}
              data-testid="jobs-newest-first"
            >
              <span className={styles.radioOuter} aria-hidden="true" />
              {newestFirstContent}
            </button>
          ) : (
            <div
              className={`${styles.orderOption} ${styles.orderOptionSelected}`}
              role="radio"
              aria-checked="true"
            >
              <span className={styles.radioOuter} aria-hidden="true">
                <span className={styles.radioInner} />
              </span>
              {newestFirstContent}
            </div>
          )}
          {bestMatchActive ? (
            <div
              className={`${styles.orderOption} ${styles.orderOptionSelected}`}
              role="radio"
              aria-checked="true"
              data-testid="jobs-best-match"
            >
              <span className={styles.radioOuter} aria-hidden="true">
                <span className={styles.radioInner} />
              </span>
              {bestMatchContent}
            </div>
          ) : (
            <a
              href="/ari"
              className={styles.orderOption}
              role="radio"
              aria-checked="false"
              onClick={handleBestMatch}
              data-testid="jobs-best-match"
            >
              <span className={styles.radioOuter} aria-hidden="true" />
              {bestMatchContent}
            </a>
          )}
        </div>
      </section>

      {bestMatchActive ? (
        <p className={styles.bestMatchNote} data-testid="jobs-best-match-note">
          Ari uses its own search criteria. Use Edit criteria to change what it
          looks for.
        </p>
      ) : (
        <>
          <section
            className={styles.section}
            aria-labelledby={sectionId('roles')}
          >
            <h3 id={sectionId('roles')} className={styles.sectionTitle}>
              Roles
            </h3>
            <ul className={styles.checkList}>
              {visibleRoleOptions.map((role) => {
                const checked = isRoleSelected(
                  filter.selectedRoles,
                  role.name,
                  role.slug
                );

                return (
                  <li key={role.slug || role.name} className={styles.checkRow}>
                    <button
                      type="button"
                      className={styles.checkButton}
                      onClick={() => onRoleToggle(role.name)}
                      aria-pressed={checked}
                    >
                      <span
                        className={`${styles.checkbox} ${
                          checked ? styles.checkboxChecked : ''
                        }`}
                        aria-hidden="true"
                      >
                        {checked && <Check size={12} strokeWidth={3} />}
                      </span>
                      <span className={styles.checkLabel}>{role.name}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {showMoreRoles && (
              <button
                type="button"
                className={styles.textButton}
                onClick={openRolesDialog}
                ref={moreRolesButtonRef}
                data-testid="jobs-more-roles"
              >
                More roles
              </button>
            )}
          </section>

          <section
            className={styles.section}
            aria-labelledby={sectionId('location')}
          >
            <h3 id={sectionId('location')} className={styles.sectionTitle}>
              Location
            </h3>
            <SingleLocationDropdown
              defaultLocation={currentLocation}
              shouldClearOnBlur={!filter.locationText}
              showResetIcon={!!filter.locationText}
              onLocationReset={handleLocationReset}
              onLocationChange={onLocationTextChange}
              placeholder="Any country or city"
              className={styles.locationDropdown}
            />
            <p className={styles.popularRow}>
              <span className={styles.popularLabel}>Popular:</span>{' '}
              {getPopularJobCountriesShort().map((country, index) => (
                <React.Fragment key={country.slug}>
                  {index > 0 && (
                    <span className={styles.popularSep} aria-hidden="true">
                      {' '}
                      ·{' '}
                    </span>
                  )}
                  <button
                    type="button"
                    className={styles.popularLink}
                    onClick={() => handlePopularCountryClick(country)}
                  >
                    {country.shortLabel}
                  </button>
                </React.Fragment>
              ))}
            </p>
            <button
              type="button"
              className={styles.toggleRow}
              onClick={onRemoteFilterToggle}
            >
              <span className={styles.toggleLabel}>Remote</span>
              <Switch
                on={!!filter.remote}
                ariaLabel="Remote"
                className={styles.brandSwitch}
              />
            </button>
          </section>

          <section
            className={styles.section}
            aria-labelledby={sectionId('experience')}
          >
            <h3 id={sectionId('experience')} className={styles.sectionTitle}>
              Experience
            </h3>
            <ul className={styles.checkList}>
              {EXPERIENCE_LEVEL_OPTIONS.map((option) => {
                const checked = isExperienceGroupChecked(
                  filter.experienceLevels,
                  option
                );
                return (
                  <li key={option.label} className={styles.checkRow}>
                    <button
                      type="button"
                      className={styles.checkButton}
                      onClick={() => onExperienceGroupToggle(option)}
                      aria-pressed={checked}
                    >
                      <span
                        className={`${styles.checkbox} ${
                          checked ? styles.checkboxChecked : ''
                        }`}
                        aria-hidden="true"
                      >
                        {checked && <Check size={12} strokeWidth={3} />}
                      </span>
                      <span className={styles.checkLabel}>{option.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          <section
            className={styles.section}
            aria-labelledby={sectionId('type')}
          >
            <h3 id={sectionId('type')} className={styles.sectionTitle}>
              Type
            </h3>
            <ul className={styles.checkList}>
              {(filter.hiringCapacities || [])
                .slice()
                .sort((a, b) => {
                  // Commercial first, then Personal
                  if (a.type === b.type) return 0;
                  if (a.type === 'commercial') return -1;
                  if (b.type === 'commercial') return 1;
                  return 0;
                })
                .map((capacity) => (
                  <li key={capacity.type} className={styles.checkRow}>
                    <button
                      type="button"
                      className={styles.checkButton}
                      onClick={() => onHiringCapacityToggle(capacity)}
                      aria-pressed={!!capacity.isChecked}
                    >
                      <span
                        className={`${styles.checkbox} ${
                          capacity.isChecked ? styles.checkboxChecked : ''
                        }`}
                        aria-hidden="true"
                      >
                        {capacity.isChecked && (
                          <Check size={12} strokeWidth={3} />
                        )}
                      </span>
                      <span className={styles.checkLabel}>
                        {capacity.label}
                      </span>
                    </button>
                  </li>
                ))}
            </ul>
          </section>

          <section className={styles.section}>
            <button
              type="button"
              className={styles.toggleRow}
              onClick={onInternalFilterToggle}
            >
              <span className={styles.toggleLabel}>Easy apply only</span>
              <Switch
                on={!!filter.internal}
                ariaLabel="Easy apply only"
                className={styles.brandSwitch}
              />
            </button>
            <button
              type="button"
              className={styles.toggleRow}
              onClick={onStatusFilterToggle}
            >
              <span className={styles.toggleLabel}>Open to applications</span>
              <Switch
                on={!!filter.status}
                ariaLabel="Open to applications"
                className={styles.brandSwitch}
              />
            </button>
          </section>
        </>
      )}

      {rolesDialogMounted && (
        <ChooseRolesDialog
          show={rolesDialogOpen}
          suggestedRoles={suggestedRoles}
          selectedRoles={filter.selectedRoles || []}
          onClose={closeRolesDialog}
          onApply={handleRolesApply}
          returnFocusRef={moreRolesButtonRef}
        />
      )}
    </div>
  );
};

JobsFilterPanel.propTypes = {
  filter: PropTypes.object.isRequired,
  suggestedRoles: PropTypes.array,
  featuredRoles: PropTypes.arrayOf(PropTypes.string),
  pageType: PropTypes.oneOf(['jobs', 'role', 'country']).isRequired,
  auth: PropTypes.oneOf(['guest', 'logged_in']).isRequired,
  filterCount: PropTypes.number,
  /** Prefix for section heading ids when the panel is mounted twice (desktop + sheet). */
  idPrefix: PropTypes.string,
  /** Desktop column shows title + Clear all; sheet uses its own header. */
  showPanelHeader: PropTypes.bool,
  onRoleToggle: PropTypes.func.isRequired,
  onRolesApply: PropTypes.func,
  onExperienceGroupToggle: PropTypes.func.isRequired,
  onHiringCapacityToggle: PropTypes.func.isRequired,
  onRemoteFilterToggle: PropTypes.func.isRequired,
  onInternalFilterToggle: PropTypes.func.isRequired,
  onStatusFilterToggle: PropTypes.func.isRequired,
  onLocationTextChange: PropTypes.func.isRequired,
  /** Immediate (non-debounced) location apply — popular country chips. */
  onLocationChange: PropTypes.func.isRequired,
  onFiltersReset: PropTypes.func.isRequired,
  onBestMatchNavigate: PropTypes.func,
  /** Leave Best match for the listing (only offered while Best match is on). */
  onNewestFirstSelect: PropTypes.func,
  /** Best match (Ari) is showing: filters hide, only ordering remains. */
  bestMatchActive: PropTypes.bool,
};

JobsFilterPanel.defaultProps = {
  suggestedRoles: [],
  featuredRoles: [],
  filterCount: 0,
  idPrefix: '',
  showPanelHeader: true,
  onRolesApply: undefined,
  onBestMatchNavigate: undefined,
  onNewestFirstSelect: undefined,
  bestMatchActive: false,
};

export default JobsFilterPanel;
